import { useQuery } from "@tanstack/react-query"
import { useAuth } from "../components/AuthProvider"
import { supabase } from "./supabase"
import { isDemoSite } from "./isDemoSite"
import { parseScoringConfig, DEFAULT_SCORING_CONFIG, type ScoringConfig } from "./scoringConfig"

/** Default label when no tenant config. */
export const DEFAULT_REPORT_LABEL = "Activity"

/** Demo site uses Luster demo branding. */
export const DEMO_REPORT_LABEL = "Activity"

export type { ScoringConfig } from "./scoringConfig"

export interface TraceNotificationConfig {
  mode: "all_admins" | "specific_emails" | "none"
  emails?: string[]
}

export interface CompanyConfig {
  report_label: string
  service_types: string[]
  property_types: string[]
  report_statuses: string[]
  checklist_templates?: Record<string, Record<string, { tasks: { task: string; completed: boolean }[] }> | { checklist: Record<string, { tasks: { task: string; completed: boolean }[] }>; service_type?: string }>
  /** IF-437: Modular scoring config. Falls back to default weights when absent. */
  scoring_config?: ScoringConfig
  /** IF-429: Min failed checklist items to trigger auto-incident. Default 3. */
  checklist_failure_threshold?: number
  /** IF-429: Controls who receives Trace auto-incident notifications. */
  trace_notification_config?: TraceNotificationConfig
}

const DEFAULT_CONFIG: CompanyConfig = {
  report_label: DEFAULT_REPORT_LABEL,
  service_types: [],
  property_types: [],
  report_statuses: [],
  scoring_config: DEFAULT_SCORING_CONFIG,
}

function mergeConfig(db: Partial<CompanyConfig> | null): CompanyConfig {
  if (!db || typeof db !== "object") return { ...DEFAULT_CONFIG }
  return {
    report_label: typeof db.report_label === "string" ? db.report_label : DEFAULT_CONFIG.report_label,
    service_types: Array.isArray(db.service_types) ? db.service_types : DEFAULT_CONFIG.service_types,
    property_types: Array.isArray(db.property_types) ? db.property_types : DEFAULT_CONFIG.property_types,
    report_statuses: Array.isArray(db.report_statuses) ? db.report_statuses : DEFAULT_CONFIG.report_statuses,
    checklist_templates: db.checklist_templates ?? DEFAULT_CONFIG.checklist_templates,
    scoring_config: parseScoringConfig(db.scoring_config),
    checklist_failure_threshold: typeof db.checklist_failure_threshold === "number" ? db.checklist_failure_threshold : undefined,
    trace_notification_config: db.trace_notification_config,
  }
}

/**
 * Fetches per-tenant company_config and merges with app defaults.
 * When unauthenticated or demo site, returns defaults (no fetch). report_label is "Service Report" unless
 * tenant has set it; demo site uses DEMO_REPORT_LABEL ("Activity Report") for display.
 */
export function useCompanyConfig(): {
  config: CompanyConfig
  reportLabel: string
  isLoading: boolean
} {
  const { companyId, isDemo } = useAuth()
  const canFetch = Boolean(companyId) && !isDemo

  const { data: raw, isLoading } = useQuery({
    queryKey: ["company-config", companyId ?? "none"],
    queryFn: async (): Promise<CompanyConfig> => {
      if (!companyId) return { ...DEFAULT_CONFIG }
      const { data, error } = await supabase
        .from("company_config")
        .select("config")
        .eq("company_id", companyId)
        .maybeSingle()
      if (error) throw error
      const config = (data?.config as Partial<CompanyConfig> | null) ?? null
      return mergeConfig(config)
    },
    enabled: canFetch,
    staleTime: Number.POSITIVE_INFINITY,
  })

  const config = raw ?? DEFAULT_CONFIG
  const reportLabel = isDemo ? DEMO_REPORT_LABEL : config.report_label

  return { config, reportLabel, isLoading: canFetch && isLoading }
}
